import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { GetMyPassengerResponse } from '../../types/auth.js'

import { apiClient } from '../../lib/client.js'
import { meKeys } from './keys.js'

export interface UpsertMyIdentityDocumentResponse {
  id: string
  documentNumber: string
  expiryDate: string
  issuingCountry: string
  type: 'CNI' | 'PASSPORT'
}

interface UpsertMyIdentityDocumentParams {
  passengerId: string
  documentNumber: string
  expiryDate: string
  issuingCountry: string
  type: 'CNI' | 'PASSPORT'
}

export const useUpsertMyIdentityDocumentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ passengerId, ...data }: UpsertMyIdentityDocumentParams) => {
      const response = await apiClient.client.post<UpsertMyIdentityDocumentResponse>(
        `/me/passengers/${passengerId}/identity-documents`,
        data
      )
      return response.data
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: meKeys.passengers })
    },
    onMutate: async ({ passengerId, ...data }) => {
      await queryClient.cancelQueries({ queryKey: meKeys.passengers })

      const previousPassengers = queryClient.getQueryData<GetMyPassengerResponse[]>(
        meKeys.passengers
      )

      if (previousPassengers) {
        queryClient.setQueryData<GetMyPassengerResponse[]>(
          meKeys.passengers,
          previousPassengers.map((p) => {
            if (p.id !== passengerId) return p

            const existingIndex = p.identityDocuments.findIndex((doc) => doc.type === data.type)
            const optimisticDoc = {
              id:
                existingIndex !== -1 ? p.identityDocuments[existingIndex].id : `temp-${Date.now()}`,
              ...data,
            }

            const updatedDocs =
              existingIndex !== -1
                ? p.identityDocuments.map((doc, i) => (i === existingIndex ? optimisticDoc : doc))
                : [...p.identityDocuments, optimisticDoc]

            return { ...p, identityDocuments: updatedDocs }
          })
        )
      }

      return { previousPassengers }
    },
  })
}
